import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { FirstAid, ForkKnife, Buildings, CheckCircle } from "@phosphor-icons/react";

const OFFICE_IMAGE = "https://images.unsplash.com/photo-1699621106755-4fe40ce95d64?q=85&w=1200&auto=format&fit=crop";

const segments = [
  { icon: FirstAid, label: "Clínicas", testid: "segment-clinics" },
  { icon: ForkKnife, label: "Restaurantes", testid: "segment-restaurants" },
  { icon: Buildings, label: "Escritórios 5+", testid: "segment-offices" },
];

const pillars = [
  {
    number: "01",
    title: "Diagnóstico antes de tudo",
    text: "Mapeamos sua infraestrutura, riscos e gargalos antes de propor qualquer solução.",
  },
  {
    number: "02",
    title: "Organização como método",
    text: "Processos documentados, inventário vivo e acessos sob controle — todos os dias.",
  },
  {
    number: "03",
    title: "Continuidade garantida",
    text: "Monitoramento proativo e plano de resposta para sua operação nunca parar.",
  },
];

export default function Differential() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="diferencial" ref={ref} data-testid="differential-section" className="py-24 lg:py-32 bg-ink text-[#FAFAFA] noise-overlay relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <p data-testid="differential-eyebrow" className="text-xs font-semibold tracking-[0.3em] uppercase text-gold mb-6">
              Diferencial
            </p>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              Continuidade de negócio
              <span className="block text-outline-gold">não é luxo.</span>
              <span className="block">É método.</span>
            </h2>
            <p data-testid="differential-text" className="mt-8 max-w-xl text-base md:text-lg text-neutral-400 leading-relaxed">
              PMEs não precisam de mais tecnologia — precisam de tecnologia organizada.
              A Kordeiro MSP assume a sua TI com processo, previsibilidade e responsabilidade,
              para você cuidar do que realmente importa: o seu negócio.
            </p>
          </motion.div>

          <div className="mt-10 flex flex-wrap gap-4" data-testid="differential-segments">
            {segments.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.span
                  key={s.label}
                  data-testid={s.testid}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 + i * 0.1 }}
                  className="inline-flex items-center gap-2.5 border border-gold/30 text-gold px-5 py-2.5 rounded-sm text-sm font-semibold tracking-wide hover:bg-gold hover:text-ink transition-colors duration-300 cursor-default"
                >
                  <Icon size={18} weight="duotone" />
                  {s.label}
                </motion.span>
              );
            })}
          </div>

          <div className="mt-14 space-y-0 border-t border-white/10" data-testid="differential-pillars">
            {pillars.map((p, i) => (
              <motion.div
                key={p.number}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                className="group flex items-start gap-6 py-7 border-b border-white/10"
                data-testid={`pillar-${p.number}`}
              >
                <span className="font-display font-black text-gold/50 text-xl pt-0.5">{p.number}</span>
                <div>
                  <h3 className="font-display font-extrabold tracking-tight text-xl flex items-center gap-3">
                    {p.title}
                    <CheckCircle size={18} weight="duotone" className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </h3>
                  <p className="text-sm text-neutral-400 mt-2 leading-relaxed max-w-md">{p.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative hidden lg:block"
        >
          <div className="absolute -inset-4 border border-gold/30 rounded-sm" aria-hidden="true" />
          <div className="relative overflow-hidden rounded-sm aspect-[4/5]" data-testid="differential-image-frame">
            <motion.img
              src={OFFICE_IMAGE}
              alt="Escritório organizado de uma pequena empresa atendida pela Kordeiro MSP"
              data-testid="differential-image"
              style={{ y: imageY, scale: 1.2 }}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
            <p className="absolute bottom-6 left-6 right-6 text-sm text-neutral-300 leading-relaxed">
              Ambientes de trabalho que funcionam — todos os dias, sem sustos.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
